import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Table } from '../components/ui/Table';
import { SubscriptionStatusBadge } from '../components/domain/StatusBadge';
import { Subscription } from '../types';
import { formatDate, getCycleName, isOverdue } from '../utils/calculations';
import { PlusIcon, SearchIcon, EyeIcon, PauseIcon, PlayIcon, XCircleIcon } from 'lucide-react';
export function Subscriptions() {
  const {
    filteredSubscriptions,
    getCustomerById,
    updateSubscription
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(false);
  // Filter subscriptions
  const filteredData = useMemo(() => {
    return filteredSubscriptions.filter(sub => {
      const customer = getCustomerById(sub.customer_id);
      const searchMatch = !searchQuery || sub.id.toLowerCase().includes(searchQuery.toLowerCase()) || customer?.name.toLowerCase().includes(searchQuery.toLowerCase());
      const statusMatch = statusFilter === 'all' || sub.status === statusFilter;
      return searchMatch && statusMatch;
    });
  }, [filteredSubscriptions, searchQuery, statusFilter, getCustomerById]);
  // Sort by next billing date
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => new Date(a.next_billing_date).getTime() - new Date(b.next_billing_date).getTime());
  }, [filteredData]);
  const handlePause = async (sub: Subscription) => {
    setIsLoading(true);
    try {
      await updateSubscription(sub.id, {
        status: 'paused'
      });
    } catch (error) {
      console.error('Failed to pause subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleResume = async (sub: Subscription) => {
    setIsLoading(true);
    try {
      await updateSubscription(sub.id, {
        status: 'active'
      });
    } catch (error) {
      console.error('Failed to resume subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleCancel = async (sub: Subscription) => {
    setIsLoading(true);
    try {
      await updateSubscription(sub.id, {
        status: 'canceled',
        end_date: new Date()
      });
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const statusOptions = [{
    value: 'all',
    label: 'All Statuses'
  }, {
    value: 'active',
    label: 'Active'
  }, {
    value: 'paused',
    label: 'Paused'
  }, {
    value: 'canceled',
    label: 'Canceled'
  }];
  const columns = [{
    key: 'id',
    header: 'Subscription',
    render: (sub: Subscription) => <Link to={`/subscriptions/${sub.id}`} className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-500">
          {sub.id.slice(0, 12)}...
        </Link>
  }, {
    key: 'customer',
    header: 'Customer',
    render: (sub: Subscription) => {
      const customer = getCustomerById(sub.customer_id);
      return <span className="font-medium" data-id="element-501">{customer?.name || 'Unknown'}</span>;
    }
  }, {
    key: 'cycle',
    header: 'Billing Cycle',
    render: (sub: Subscription) => getCycleName(sub.cycle_days)
  }, {
    key: 'nextBilling',
    header: 'Next Billing',
    render: (sub: Subscription) => {
      const overdue = sub.status === 'active' && isOverdue(sub.next_billing_date);
      return <span className={overdue ? 'text-red-600 font-medium' : ''} data-id="element-502">
            {formatDate(sub.next_billing_date)}
            {overdue && ' (Overdue)'}
          </span>;
    }
  }, {
    key: 'status',
    header: 'Status',
    render: (sub: Subscription) => <SubscriptionStatusBadge status={sub.status} data-id="element-503" />
  }, {
    key: 'actions',
    header: '',
    render: (sub: Subscription) => <div className="flex items-center justify-end gap-1" data-id="element-504">
          <Link to={`/subscriptions/${sub.id}`} data-id="element-505">
            <Button variant="ghost" size="sm" aria-label="View subscription" data-id="element-506">
              <EyeIcon className="w-4 h-4" data-id="element-507" />
            </Button>
          </Link>
          {sub.status === 'active' && <Button variant="ghost" size="sm" onClick={e => {
        e.stopPropagation();
        handlePause(sub);
      }} disabled={isLoading} aria-label="Pause subscription" data-id="element-508">
              <PauseIcon className="w-4 h-4" data-id="element-509" />
            </Button>}
          {sub.status === 'paused' && <Button variant="ghost" size="sm" onClick={e => {
        e.stopPropagation();
        handleResume(sub);
      }} disabled={isLoading} className="text-green-600 hover:text-green-700 hover:bg-green-50" aria-label="Resume subscription" data-id="element-510">
              <PlayIcon className="w-4 h-4" data-id="element-511" />
            </Button>}
          {sub.status !== 'canceled' && <Button variant="ghost" size="sm" onClick={e => {
        e.stopPropagation();
        handleCancel(sub);
      }} disabled={isLoading} className="text-red-600 hover:text-red-700 hover:bg-red-50" aria-label="Cancel subscription" data-id="element-512">
              <XCircleIcon className="w-4 h-4" data-id="element-513" />
            </Button>}
        </div>
  }];
  return <div className="space-y-6" data-id="element-514">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-515">
        <div data-id="element-516">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-517">Subscriptions</h1>
          <p className="text-gray-500 mt-1" data-id="element-518">
            {sortedData.length} subscription{sortedData.length !== 1 ? 's' : ''}{' '}
            found
          </p>
        </div>
        <Link to="/subscriptions/new" data-id="element-519">
          <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-521" />} data-id="element-520">
            New Subscription
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card data-id="element-522">
        <div className="flex flex-col sm:flex-row gap-4" data-id="element-523">
          <div className="flex-1 relative" data-id="element-524">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-525" />
            <Input placeholder="Search by subscription ID or customer..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-526" />
          </div>
          <div className="w-full sm:w-48" data-id="element-527">
            <Select options={statusOptions} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-528" />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Table columns={columns} data={sortedData} keyExtractor={sub => sub.id} emptyMessage="No subscriptions found" data-id="element-529" />
    </div>;
}